import type { HotelDetails, HotelResult, RoomRate } from '@niarab2c/frontend-commons/src/types/hotels'
import type { CriteriaForm } from '@niarab2c/niarab2c-core/src/app/base'
import type { User } from '@niarab2c/niarab2c-core/src/app/reducers/authentication'
import type { PersonalData, ShoppingCartState } from '@niarab2c/niarab2c-core/src/app/reducers/shoppingCart'
import { Storefront } from '@niarab2c/otabuilder-crudmodel/src'
import { diffInDays, testLocalStorage } from '@niaratech/niara-js-commons'
import { facebookTrack } from '@niaratech/niara-react-components/lib/metapixel'
import type { PaymentFormV2Props } from '@niaratech/ota-components/src/CheckoutForm/PaymentFormV2'
import meanBy from 'lodash/meanBy'
import _pick from 'lodash/pick'
import sumBy from 'lodash/sumBy'
import type {
  CurrentClient,
  CurrentTenant,
  Order,
  SelfHotelReservation,
  SelfOrder,
  ShallowClient,
} from 'niara-spear-crudmodel/src'
import queryString from 'query-string'

const DATA_LAYER_NAME = 'dataLayer'
const dataLayer = typeof window == 'undefined' ? [] : (window[DATA_LAYER_NAME] = window[DATA_LAYER_NAME] || [])

function gtag(..._: any[]) {
  // PS: Tentei passar _ ao invés de arguments, mas não funcionou!
  // eslint-disable-next-line prefer-rest-params
  dataLayer.push(arguments)
}
let datalayerVersion: 'otabuilder_v1' | 'otabuilder_v2' = 'otabuilder_v1'

export const setDatalayerVersion = (v: typeof datalayerVersion) => (datalayerVersion = v || 'otabuilder_v1')

export const maskDocument = (document: string | null) => {
  if (document && document?.length > 0) {
    document = document.replaceAll(/[+-]/g, '')
    const shownCharacters = Math.max(Math.min(4, document?.length - 4), 2)
    return (
      '*'.repeat(Math.max(document.length - shownCharacters, 0)) + document.substring(document.length - shownCharacters)
    )
  } else {
    return document
  }
}

export const maskPhone = (document: string | null) => {
  if (document && document?.length > 0) {
    const shownPrefix = 4
    const shownSuffix = Math.max(Math.min(document.length - shownPrefix, 2), 0)
    return (
      document.substring(0, shownPrefix) +
      '*'.repeat(Math.max(document.length - shownPrefix - shownSuffix, 0)) +
      document.substring(document.length - shownSuffix)
    )
  } else {
    return document
  }
}

function pushToDataLayer(stuff: any): void {
  dataLayer.push(stuff)
}

const catchAnyErrorFunction = function <P extends Array<any>, R>(func: { (...p: P): R }): { (...p: P): R } {
  return (...p) => {
    try {
      return func(...p)
    } catch (ignore) {
      //TODO
      // eslint-disable-next-line no-console
      console.error(ignore)
    }
  }
}

type ListName = 'Search Results' | 'Hotel Details'

// Referências:
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#view_item
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item
// https://support.google.com/analytics/answer/9143382#dimensions
// https://developers.google.com/tag-manager/ecommerce-ga4?hl=pt-br#measure_productitem_list_viewsimpressions

const buildItem = (
  hotelResult: Pick<HotelResult, 'clientId' | '_id' | 'hotel'>,
  roomRate: RoomRate,
  listName?: ListName,
  index?: number
) => {
  if (!hotelResult) return undefined
  const hotel = hotelResult?.hotel
  return {
    item_name: hotel.name,
    item_id: hotel.id,
    price: roomRate ? roomRate?.priceComposition?.productPrice?.value : undefined,
    currency: roomRate ? roomRate?.priceComposition?.productPrice?.currency : undefined,
    item_category: 'Hotel',
    item_variant: roomRate ? roomRate?.time?.startDate : undefined,
    item_list_name: listName,
    index: index,
    quantity: 1,
  }
}

const buildItem2 = (orderItem: Order['hotelReservations'][0]) => {
  return {
    item_name: orderItem.productName,
    item_id: orderItem.productId,
    price: orderItem.totalPrice,
    currency: orderItem.currencyCode,
    item_category: 'Hotel',
    item_variant: orderItem.startDate,
    quantity: 1,
  }
}

const buildBuyerData = (buyer: PersonalData['buyer'] | User, selfBuyer?: PersonalData['buyer'] | User) => {
  const getPropData = (propInBuyer: string, propInUser?: string): string =>
    buyer?.[propInBuyer] ?? buyer?.[propInUser] ?? selfBuyer?.[propInBuyer] ?? selfBuyer?.[propInUser] ?? null

  return {
    firstName: getPropData('firstName'), // '{{FIRST-NAME}}',
    lastName: getPropData('lastName'), // '{{LAST-NAME}}',
    userEmail: getPropData('email'), // '{{USER-EMAIL}}',
    userTel: getPropData('phone'), //  '{{USER-TEL}}',
    userNationality: getPropData('nationality'),
    userCountry: getPropData('country'),
    userAddressCountryCode: null, // TODO como pegar essa informação? #232666
    userAddressCity: getPropData('addressCity'),
    userAddressComplement: getPropData('addressComplement', 'addressAdditionalInformation'),
    userAddressDistrict: getPropData('addressDistrict'),
    userAddressNumber: getPropData('addressNumber'),
    userAddressState: getPropData('addressState'),
    userAddressStreet: getPropData('addressStreet', 'addressStreetName'),
    userAddressZipCode: getPropData('addressZipCode'),
    userDob: getPropData('dob'),
    userPassport: getPropData('passport'),
    userPersonId: getPropData('personId'),
    userRg: getPropData('rg'),
    userRgOrgaoEmissor: getPropData('rgOrgaoEmissor'),
    userVatNumber: getPropData('vatNumber'),
    userBrandName: getPropData('brandName'),
    userCorporateName: getPropData('corporateName'),
    userInscricaoMunicipal: getPropData('inscricaoMunicipal'),
    userInscricaoEstadual: getPropData('inscricaoEstadual'),
    userDoc: getPropData('cpf'),
  }
}

type Occupancy = { adults: number; children?: number; childrenAges?: number[] }

type CartItem = {
  occupancy: Occupancy
  roomRate: RoomRate
  roomType: string
  hotelId: string
  quantity: number
  startDate: string
  endDate: string
  hotelDetails: HotelDetails
  priceComposition: Pick<SelfHotelReservation['roomStays'][0]['price'], 'nights' | 'taxes' | 'total'> &
    Pick<RoomRate['priceComposition'], 'discounts'>
}

const getLocale = (defaultLocale?: string) => {
  const LOCAL_STORAGE_AVAILABLE = testLocalStorage()
  if (!LOCAL_STORAGE_AVAILABLE) return null
  return localStorage.getItem('locale') != null ? localStorage.getItem('locale') : defaultLocale
}

export const getGA4Session = (streamId: string): { id: number; number: number } => {
  if (!streamId) return null

  const measurementId = streamId.split('-')?.[1]
  if (!measurementId) return null

  const pattern = new RegExp(`_ga_${measurementId}=GS\\d\\.\\d\\.(.+?)(?:;|$)`)
  const match = document.cookie.match(pattern)
  const parts = match?.[1].split('.')

  if (!parts) return null

  const id = parseInt(parts.shift())
  const number = parseInt(parts.shift())

  if (isNaN(id)) {
    return null
  }

  return {
    id,
    number: isNaN(number) ? null : number,
  }
}

function promiseWithTimeout<T = any>(
  timeout,
  callback: { (resolve: { (value: T | PromiseLike<T>): void }): void }
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    // Set up the timeout
    const timer = setTimeout(() => {
      reject(new Error(`Promise timed out after ${timeout} ms`))
    }, timeout)

    // Set up the real work
    callback((value) => {
      clearTimeout(timer)
      resolve(value)
    })
  })
}

/**
 * https://support.google.com/analytics/answer/10071811?hl=en#zippy=%2Cmanual-setup
 * https://developers.google.com/tag-platform/gtagjs/reference?sjid=894996386119288423-SA
 *
 * @param ga4TagId
 * @returns
 */
export const getGA4SessionAndClient = async (
  ga4TagId: string
): Promise<{ ga4ClientId?: string; ga4SessionId?: string; ga4TagId?: string }> => {
  if (!ga4TagId) {
    return {}
  }
  const [ga4ClientId, ga4SessionId] = await Promise.all([
    promiseWithTimeout(500, (resolve) => {
      gtag('get', ga4TagId, 'client_id', resolve)
    }),
    promiseWithTimeout(500, (resolve) => {
      gtag('get', ga4TagId, 'session_id', resolve)
    }),
  ])
  return { ga4ClientId, ga4SessionId, ga4TagId }
}

export const configGA4SessionAndClientFromUrl = (): void => {
  const params = Object.assign({}, queryString.parse(location.hash), queryString.parse(location.search))
  const { ga4ClientId, ga4SessionId, ga4TagId } = params
  if (ga4TagId && ga4ClientId && ga4SessionId) {
    gtag('config', ga4TagId, {
      client_id: ga4ClientId,
      session_id: ga4SessionId,
    })
  }
}

export const trackTenantAndClient = catchAnyErrorFunction(
  (currentTenant: CurrentTenant, currentClient: CurrentClient | ShallowClient): void => {
    const tenant = currentTenant
      ? _pick(
          currentTenant,
          'tenantId',
          'id',
          'alias',
          'DevOrHomologOrProd',
          'country_code',
          'isBee2Pay',
          'modules_crs',
          'systemLocale',
          'name'
        )
      : null
    const client = currentClient ? _pick(currentClient, 'brand_name', 'id', 'country_code', 'systemLocale') : null
    pushToDataLayer({
      event: 'set_tenant_and_client',
      tenantId: tenant?.tenantId,
      clientId: client?.id,
      tenant,
      client,
    })
  }
)

export const trackUserId = catchAnyErrorFunction((userId: string, event = 'set_user_id'): void => {
  pushToDataLayer({
    user_id: userId,
    event,
  })
})

export const trackEventHomepage = catchAnyErrorFunction((storefront: Storefront, tenant: CurrentTenant): void => {
  // Event 0 - Landing Page
  // Call this function when a user enters the homepage of a storefront?.

  pushToDataLayer({
    event: 'landingPage',
    clientName: storefront?.clientName, // '{{CLIENT-NAME}}'
    clientProject: storefront?.landingPageId, // '{{CLIENT-PROJECT}}',
    location: tenant?.city, // '{{NAME-CITY}}', ?
    versionBE: datalayerVersion, // '{{VERSION-BE}}',
    ecommerce: {
      // currency
      items:
        storefront?.widgets_homepage
          ?.map((widget) =>
            widget?.content
              ?.filter((c) => c?.hotelId)
              .map((content) => ({
                item_name: content?.title, // '{{HOTEL-NAME}}',
                item_id: content?.hotelId, // '{{HOTEL ID}}',
                location: content?.location, // '{{HOTEL-LOCATION}}',
                // price: '{{PRODUCT-PRICE}}', - não tenho preço nos widgets
                // item_brand: '{{CHAIN-NAME}}', - não tem nos widgets
                list: 'Featured Hotels',
                affiliation: getAffiliation(content?.hotelId) ?? storefront?.name,
              }))
          )
          ?.flat()
          .map((item, index) => ({
            ...item,
            index: index + 1,
          })) ?? [],
    },
  })
})

/**
 *
 * @param date 2025-05-20
 * @returns 20250520
 */
const clearDateToDataLayer = (date?: string): string => {
  if (!date) return null
  return date.replace(/-/g, '')
}

/**
 *
 * @param hotelId HOTEL_OMNI_123456
 * @returns 123456
 */
const clearHotelIdToDataLayer = (hotelId?: string): string => {
  if (!hotelId) return null
  return hotelId?.startsWith('HOTEL_OMNI_') ? hotelId?.replace(/HOTEL_OMNI_/, '') : hotelId
}

/**
 *
 * @param chainId CHAIN_OMNI_123456
 * @returns 123456
 */
const clearChainIdToDataLayer = (chainId?: string): string => {
  if (!chainId) return null
  return chainId?.startsWith('CHAIN_OMNI_') ? chainId?.replace(/CHAIN_OMNI_/, '') : chainId
}

const getChainIdAndChainName = (items: Array<{ id?: string; name?: string }>) =>
  items?.every((i) => i?.id == items?.[0]?.id)
    ? { id: items?.[0]?.id, name: items?.[0]?.name }
    : { id: null, name: null }

const getAffiliation = (hotelId?: string): 'Omnibees' | null => {
  if (!hotelId) return null
  return hotelId?.startsWith?.('HOTEL_OMNI_') ? 'Omnibees' : null
}

// Não possui evento da Omnibees
/**
 ** Passou a disparar o evento ecommerceChain em 21/05/2025 #222243
 ** Evento anterior: ecommerceSearch
 */
export const trackEventHoteList = catchAnyErrorFunction(
  (
    results: HotelResult[],
    searchCriteria: CriteriaForm,
    buyer: PersonalData['buyer'] | User,
    storefront: Storefront
  ): void => {
    // Event 1.1 - Search Results
    //   Call this function when a the Search Page receives new information. This function uses the event
    //   callback datalayer variable to handle navigation after the ecommerce data has been sent
    //   to Google Analytics.
    if (!results?.[0]) return

    const currencyCode = results?.[0]?._bestPriceRoomRate?.priceComposition?.total?.currency

    pushToDataLayer({ ecommerce: null }) // Clear the previous ecommerce object.

    const chain = getChainIdAndChainName(results?.map((r) => ({ id: r?.hotel?.chainId, name: r?.hotel?.chainName })))

    pushToDataLayer({
      // event: 'ecommerceChain',
      event: datalayerVersion == 'otabuilder_v1' ? 'ecommerceSearch' : 'ecommerceChain', // view_item_list ??
      eventDetails: {
        category: 'Ecommerce',
        action: 'Cadeia Hoteleira',
      },
      versionBE: datalayerVersion, // '{{VERSION-BE}}',
      chainName: chain?.name, // '{{CHAIN-NAME}}'
      chainId: clearChainIdToDataLayer(chain?.id), // '{{CHAIN-ID}}'
      isRemark: null, // true || false
      lang: getLocale(storefront?.i18n_defaultLocale), // '{{LANG-BE}}'
      check_in: clearDateToDataLayer(searchCriteria?.startDate), // '{{CHECK-IN}}',
      check_out: clearDateToDataLayer(searchCriteria?.endDate), // '{{CHECK-OUT}}',
      checkIn: clearDateToDataLayer(searchCriteria?.startDate), // '{{CHECK-IN}}',
      checkOut: clearDateToDataLayer(searchCriteria?.endDate), // '{{CHECK-OUT}}',
      coupon: searchCriteria?.promoCode, // '{{COUPON-CODE}}',
      numberAdults: sumBy(searchCriteria?.rooms, (room) => room.adults), // '{{NUMBER-OF-ADULTS}}',
      numberChildren: sumBy(searchCriteria?.rooms, (room) => room.children), // '{{NUMBER-OF-CHILDREN}}',
      ...buildBuyerData(buyer),
      ecommerce: {
        currency: currencyCode,
        items: results.map((hotelResult, i) => ({
          item_id: clearHotelIdToDataLayer(hotelResult?.hotel?.id), //'{{HOTEL-ID}}',
          item_name: hotelResult?.hotel?.name, // '{{HOTEL-NAME}}',
          // item_variant:
          item_brand: hotelResult?.hotel?.chainName,
          item_category: 'Hotel',
          // affiliation:
          coupon: searchCriteria?.promoCode, //'{{COUPON-CODE}}
          price: hotelResult?._bestPriceRoomRate?.priceComposition?.total?.value, // '{{PRODUCT-PRICE}}',
          //discount:,
          quantity: 1,
          index: i,
          item_list_name: 'Hotel List',
          //NOT IN DOC OMNIBEES
          currency: hotelResult?._bestPriceRoomRate?.priceComposition?.total?.currency, // sent by niara / motor omnibees
          item_availability: hotelResult?._bestPriceRoomRate?.available && 'Available for Sale', // existe mensagem de não disponibilidade? sent by niara / motor omnibees
          item_rate_id: hotelResult?._bestPriceRoomRate?.ratePlan?.id, // sent by niara / motor omnibees
          location: hotelResult?.hotel?.cityName, // sent by niara
          niaraHotelId: hotelResult?.hotel?.id, // sent by niara '{{HOTEL-ID}}'
        })),
        //OLD SUPPORT
        currencyCode: currencyCode,
        impressions: results.map((hotelResult, i) => ({
          name: hotelResult?.hotel?.name, //'{{PRODUCT-NAME}}'
          id: clearHotelIdToDataLayer(hotelResult?.hotel?.id), //'{{PRODUCT-SKU}}',
          price: hotelResult?._bestPriceRoomRate?.priceComposition?.total?.value, //'{{PRODUCT-PRICE}}',
          category: 'Hotel',
          brand: hotelResult?.hotel?.chainName, //'{{BRAND}}',
          list: 'Hotel List',
          position: i,
          //NOT IN DOC OMNIBEES
          niaraHotelId: hotelResult?.hotel?.id, // sent by niara '{{HOTEL-ID}}'
        })),
      },
      //NOT IN DOC OMNIBEES
      niaraChainId: chain?.id, // sent by niara '{{CHAIN-ID}}'
      clientName: storefront?.clientName, // sent by niara '{{CLIENT-NAME}}'
      clientProject: storefront?.landingPageId, // sent by niara '{{CLIENT-PROJECT}}',
      location: searchCriteria?.destinationName,
      daysAdvance: diffInDays(searchCriteria?.startDate, new Date()), // {{DAYS-IN-ADVANCE}}
    })

    facebookTrack('ViewContent', {
      content_type: 'view_item_list',
      contents: {
        currencyCode: currencyCode,
        items: results
          ?.map((hotelResult, index) =>
            buildItem(hotelResult, hotelResult?._bestPriceRoomRate, 'Search Results', index)
          )
          .filter(Boolean),
      },
      currency: currencyCode,
    })
  }
)

export const trackEventHotelClick = catchAnyErrorFunction(
  (hotelResult: HotelResult, roomRate: RoomRate, listName?: ListName, index?: number): void => {
    //   Call this function when a user clicks on a product link. This function uses the event
    //   callback datalayer variable to handle navigation after the ecommerce data has been sent
    //   to Google Analytics.
    if (!hotelResult || !roomRate) return

    pushToDataLayer({ ecommerce: null }) // Clear the previous ecommerce object.
    pushToDataLayer({
      event: 'select_item',
      ecommerce: {
        items: [buildItem(hotelResult, roomRate, listName, index)],
      },
    })

    facebookTrack('ViewContent', {
      // content_ids,
      // content_category,
      // content_name: 'select_item',
      content_type: 'select_item',
      contents: {
        items: [buildItem(hotelResult, roomRate, listName, index)],
      },
      // currency,
      // value
    })
  }
)

/**
 ** Passou a disparar o evento ecommerceSearch em 21/05/2025 #222243
 ** Evento anterior: ecommerceHotelDetails
 */
export const trackEventHotelDetails = catchAnyErrorFunction(
  (
    hotelResult: HotelResult,
    searchCriteria: CriteriaForm,
    buyer: PersonalData['buyer'] | User,
    storefront: Storefront
  ): void => {
    // Event 1.2 - Hotel Details
    // Measure a view of product details. This example assumes the detail view occurs on pageload,
    // and also tracks a standard pageview of the details page.
    const { hotel, _bestPriceRoomRate } = hotelResult
    if (!hotel || !_bestPriceRoomRate) return

    // const currencyCode = hotelResult?.[0]?._bestPriceRoomRate?.priceComposition?.total?.currency
    const currencyCode = hotelResult?._bestPriceRoomRate?.priceComposition?.total?.currency
    const numberRooms = searchCriteria?.rooms?.length

    pushToDataLayer({ ecommerce: null }) // Clear the previous ecommerce object.

    pushToDataLayer({
      event: datalayerVersion == 'otabuilder_v1' ? 'ecommerceHotelDetails' : 'ecommerceSearch',
      eventDetails: {
        category: 'Ecommerce',
        action: 'Resultados de Pesquisa',
      },
      versionBE: datalayerVersion, // '{{VERSION-BE}}',
      chainName: hotelResult?.hotel?.chainName, // '{{CHAIN-NAME}}'
      chainId: clearChainIdToDataLayer(hotelResult?.hotel?.chainId), // '{{CHAIN-ID}}'
      isRemark: null, // true || false
      lang: getLocale(storefront?.i18n_defaultLocale), // '{{LANG-BE}}'
      check_in: clearDateToDataLayer(searchCriteria?.startDate), // '{{CHECK-IN}}',
      check_out: clearDateToDataLayer(searchCriteria?.endDate), // '{{CHECK-OUT}}',
      checkIn: clearDateToDataLayer(searchCriteria?.startDate), // '{{CHECK-IN}}',
      checkOut: clearDateToDataLayer(searchCriteria?.endDate), // '{{CHECK-OUT}}',
      coupon: searchCriteria?.promoCode, // '{{COUPON-CODE}}',
      numberAdults: sumBy(searchCriteria?.rooms, (room) => room.adults), // '{{NUMBER-OF-ADULTS}}',
      numberChildren: sumBy(searchCriteria?.rooms, (room) => room.children), // '{{NUMBER-OF-CHILDREN}}',
      hotelName: hotelResult?.hotel?.name, // '{{HOTEL-NAME}}',
      hotelId: clearHotelIdToDataLayer(hotelResult?.hotel?.id), //'{{HOTEL-ID}}',
      hotelCategoryId: '', // onde pegar esse valor? '{{HOTEL-CATEGORY-ID}}',
      hotelCategoryName: '', // onde pegar esse valor? '{{HOTEL-CATEGORY-NAME}}',
      ratingStars: hotelResult?.hotel?.award, // '{{NUMBER-STAR}}',
      numberRooms, // '{{TOTAL-ROOMS-OF-HOTEL}}',
      ...buildBuyerData(buyer),
      // isLoggedIn: '{{USER_IS_LOGGED}}',
      // loyalty: {
      //   loyalty: '{{LOYALTY}}',
      //   loyalty_category: '{{LOYALTY_CATEGORY}}',
      //   loyalty_level: '{{LOYALTY_LEVEL}}',
      //   loyalty_name: '{{LOYALTY_DESCRIPTION}}',
      //   loyalty_discount_value: '{{VALUE}}',
      //   loyalt_type: '{{LOYALTY_TYPE}}',
      // },
      ecommerce: {
        currency: currencyCode,
        items: hotelResult?.roomRates?.map((rate, i) => ({
          item_id: rate?.roomType?.id, // '{{ROOM-ID}}',
          item_name: rate?.roomType?.name, // '{{ROOM-NAME}}',
          item_variant: rate?.ratePlan?.name, // '{{RATE-ROOM}}'
          item_brand: hotelResult?.hotel?.name, // '{{HOTEL-NAME}}'
          item_category: 'Accommodation', //'{{Accommodation OR ROOM OR EXTRA}}'
          affiliation: getAffiliation(hotelResult?.hotel?.id) ?? storefront?.name,
          coupon: searchCriteria?.promoCode, //'{{COUPON-CODE}}
          price: rate?.priceComposition?.total?.value, // '{{ROOM-PRICE}}',
          discount: rate?.priceComposition?.discounts?.value, //'{{DISCOUNT-VALUE}}'
          quantity: 1,
          index: i,
          item_list_name: 'Search Results',
          //NOT IN DOC OMNIBEES
          currency: rate?.priceComposition?.total?.currency, // sent by niara / motor omnibees
          item_availability: rate?.available && 'Available for Sale', // existe mensagem de não disponibilidade? sent by niara / motor omnibees
          item_rate_id: rate?.ratePlan?.id, // sent by niara / motor omnibees
          location: hotelResult?.hotel?.cityName, // sent by niara
        })),
        //OLD SUPPORT
        currencyCode: currencyCode,
        impressions: hotelResult?.roomRates?.map((rate, i) => ({
          name: rate?.roomType?.name, //'{{PRODUCT-NAME}}'
          id: rate?.roomType?.id, //'{{PRODUCT-SKU}}',
          price: rate?.priceComposition?.total?.value, //'{{PRODUCT-PRICE}}',
          category: 'Accommodation', // '{{CATEGORY}}',
          brand: hotelResult?.hotel?.chainName, //'{{BRAND}}',
          list: 'Search Results',
          position: i,
        })),
      },

      //NOT IN DOC OMNIBEES
      niaraHotelId: hotelResult?.hotel?.id, // sent by niara '{{HOTEL-ID}}',
      niaraChainId: hotelResult?.hotel?.chainId, // sent by niara '{{CHAIN-ID}}',
      clientName: storefront?.clientName, // sent by niara '{{CLIENT-NAME}}'
      clientProject: storefront?.landingPageId, // sent by niara '{{CLIENT-PROJECT}}',
      location: hotelResult?.hotel?.cityName, // sent by niara '{{NAME-CITY}}',
      daysAdvance: diffInDays(searchCriteria?.startDate, new Date()), // {{DAYS-IN-ADVANCE}}
    })

    facebookTrack('ViewContent', {
      // content_ids,
      // content_category,
      // content_name: 'view_item',
      content_type: 'view_item',
      contents: {
        items: [buildItem(hotelResult, hotelResult?._bestPriceRoomRate, 'Hotel Details', undefined)],
      },
      // currency,
      // value
    })
  }
)

export const trackEventAddItemToCart = catchAnyErrorFunction(
  (
    hotelResult: Pick<HotelResult, 'clientId' | '_id' | 'hotel'>,
    roomRates: RoomRate[],
    buyer?: PersonalData['buyer'] | User,
    storefront?: Storefront
  ): void => {
    // Other Events - Add to Cart
    // Measure adding a product to a shopping cart by using an 'add' actionFieldObject
    // and a list of productFieldObjects.

    const { hotel } = hotelResult
    if (!hotel || !roomRates) return

    pushToDataLayer({ ecommerce: null }) // Clear the previous ecommerce object.

    // Não é necessário colocar esse map para dentro do "items" porque apenas um item entra por vez no carrinho
    roomRates.map((roomRate) => {
      pushToDataLayer({
        event: 'ecommerceAddToCart',
        hotelId: clearHotelIdToDataLayer(hotel?.id),
        hotelName: hotel?.name,
        chainId: clearChainIdToDataLayer(hotel?.chainId),
        chainName: hotel?.chainName,
        isRemark: null,
        lang: getLocale(storefront?.i18n_defaultLocale),
        ecommerce: {
          currency: roomRate?.priceComposition?.productPrice?.currency, //'{{CURRENCY}}',
          currencyCode: roomRate?.priceComposition?.productPrice?.currency, //'{{CURRENCY}}',
          value: roomRate?.priceComposition?.productPrice?.value, // (PRICE-ROOM) * (QUANTITY): NO TAXES
          items: [
            {
              item_id: roomRate?.roomType?.id, // {{ ROOM-ID }}
              item_name: roomRate?.roomType?.name, // {{ ROOM-NAME }}
              item_variant: roomRate?.ratePlan?.name, // {{ ROOM-RATE }}
              item_brand: hotel?.name, // {{ HOTEL-NAME }}
              item_category: 'Accommodation', // ROOM OR EXTRA OR Accommodation
              affiliation: getAffiliation(hotelResult?.hotel?.id) ?? storefront?.name,
              coupon: roomRate?.promoCode, // "{{COUPON-CODE}}",
              price: meanBy(roomRate?.priceComposition?.nights, (night) => night.value), // DAILY MAIN PRICE
              discount: roomRate?.priceComposition?.discounts?.value, // {{ DISCOUNT-VALUE}}
              quantity: roomRate?.priceComposition?.nights?.length, // NUMBER OF NIGHTS BOOKED

              //NOT IN DOC OMNIBEES
              item_list_id: hotel?.chainId, // sent by niara {{CHAIN-ID}}
              item_list_name: hotel?.chainName, // sent by niara {{CHAIN-NAME}}
              item_category2: hotel?.award, // sent by niara {{N-STAR_HOTEL}}
              item_category3: '', // sent by niara {{HOTEL-CATEGORY}}
              currency: roomRate?.priceComposition?.total?.currency, // sent by niara
              check_in: clearDateToDataLayer(roomRate?.time?.startDate), // sent by niara {{ CHECK-IN }}
              check_out: clearDateToDataLayer(roomRate?.time?.endDate), // sent by niara {{ CHECK-OUT }}
            },
          ],
        },
        ...buildBuyerData(buyer),
        //NOT IN DOC OMNIBEES
        niaraHotelId: hotel?.id, // sent by niara
        niaraChainId: hotel?.id, // sent by niara
        location: hotelResult?.hotel?.cityName, // sent by niara '{{NAME-CITY}}',
      })

      facebookTrack('AddToCart', {
        // content_ids,
        // content_name: 'view_item',
        content_type: 'add_to_cart',
        contents: {
          currency: roomRate?.priceComposition?.total.currency,
          value: roomRate?.priceComposition?.total.value,
          items: [buildItem(hotelResult, roomRate, 'Hotel Details', undefined)],
        },
        currency: roomRate?.priceComposition?.total.currency,
        value: roomRate?.priceComposition?.total.value,
      })
    })
  }
)

// Não tem DataLayer da Omnibees especificado
export const trackEventRemoveItemFromCart = catchAnyErrorFunction(
  (
    hotelResult: Pick<HotelResult, 'clientId' | '_id' | 'hotel'>,
    roomRate: RoomRate,
    buyer?: PersonalData['buyer'] | User,
    storefront?: Storefront
  ): void => {
    // Other Events - Remove from Cart
    // Measure adding a product to a shopping cart by using an 'add' actionFieldObject
    // and a list of productFieldObjects.
    const { hotel } = hotelResult
    if (!hotel || !roomRate) return

    pushToDataLayer({ ecommerce: null }) // Clear the previous ecommerce object.
    pushToDataLayer({
      event: 'ecommerceRemoveFromCart',
      location: hotelResult?.hotel?.cityName, // '{{NAME-CITY}}',
      chainId: clearChainIdToDataLayer(hotelResult?.hotel?.chainId),
      chainName: hotelResult?.hotel?.chainName,
      hotelId: clearHotelIdToDataLayer(hotelResult?.hotel?.id),
      hotelName: hotelResult?.hotel?.name,
      isRemark: null,
      // clientName: null, // '{{CLIENT-NAME}}',
      // clientProject: null, // '{{CLIENT-PROJECT}}',
      ecommerce: {
        currency: roomRate?.priceComposition?.productPrice?.currency, //'{{CURRENCY}}',
        currencyCode: roomRate?.priceComposition?.productPrice?.currency, //'{{CURRENCY}}',
        value: roomRate?.priceComposition?.productPrice?.value, // (PRICE-ROOM) * (QUANTITY): NO TAXES
        items: [
          {
            affiliation: getAffiliation(hotelResult?.hotel?.id) ?? storefront?.name,
            coupon: roomRate?.promoCode, // "{{COUPON-CODE}}",
            discount: roomRate?.priceComposition?.discounts?.value, // {{ DISCOUNT-VALUE}}
            item_brand: hotel?.name, // {{ HOTEL-NAME }}
            item_category: 'Accommodation', // Accommodation OR ROOM OR EXTRA
            item_id: roomRate?.roomType?.id, // {{ ROOM-ID }}
            item_name: roomRate?.roomType?.name, // {{ ROOM-NAME }}
            item_variant: roomRate?.ratePlan?.name, // {{ ROOM-RATE }}
            price: meanBy(roomRate?.priceComposition?.nights, (night) => night.value), // DAILY MAIN PRICE
            quantity: roomRate?.priceComposition?.nights?.length, // NUMBER OF NIGHTS BOOKED

            //NOT IN DOC OMNIBEES
            item_list_id: clearChainIdToDataLayer(hotel?.chainId), // sent by niara {{CHAIN-ID}}
            niaraChainId: hotel?.chainId,
            item_list_name: hotel?.chainName, // sent by niara {{CHAIN-NAME}}
            check_in: clearDateToDataLayer(roomRate?.time?.startDate), // sent by niara {{ CHECK-IN }}
            check_out: clearDateToDataLayer(roomRate?.time?.endDate), // sent by niara {{ CHECK-OUT }}
            checkIn: clearDateToDataLayer(roomRate?.time?.startDate), // sent by niara {{ CHECK-IN }}
            checkOut: clearDateToDataLayer(roomRate?.time?.endDate), // sent by niara {{ CHECK-OUT }}
            item_category2: hotel?.award, // sent by niara {{N-STAR_HOTEL}}
            item_category3: '', // sent by niara {{HOTEL-CATEGORY}}
            currency: roomRate?.priceComposition?.total?.currency,
          },
        ],
      },
      firstName: buyer?.firstName, // '{{FIRST-NAME}}',
      lastName: buyer?.lastName, // '{{LAST-NAME}}',
      userEmail: buyer?.email, // '{{USER-EMAIL}}',
      //NOT IN DOC OMNIBEES
      niaraChainId: hotelResult?.hotel?.chainId, // sent by niara
      niaraHotelId: hotelResult?.hotel?.id, // sent by niara
    })

    facebookTrack('AddToCart', {
      // content_ids,
      // content_name: 'view_item',
      content_type: 'remove_from_cart',
      contents: {
        currency: roomRate?.priceComposition?.total.currency,
        value: roomRate?.priceComposition?.total.value,
        items: [buildItem(hotelResult, roomRate, 'Hotel Details', undefined)],
      },
      currency: roomRate?.priceComposition?.total.currency,
      value: roomRate?.priceComposition?.total.value,
    })
  }
)

export const trackEventNotFoundInRefreshCart = catchAnyErrorFunction(
  (
    hotelResult: Pick<HotelResult, 'clientId' | '_id' | 'hotel'>,
    roomRates: RoomRate[],
    buyer?: PersonalData['buyer'],
    storefront?: Storefront
  ): void => {
    // Other Events - Not found in refresh cart
    // Quando um item do carrinho perde a disponibilidade dentro do checkout ao executar o Refresh Cart

    const { hotel } = hotelResult
    if (!hotel || !roomRates) return

    pushToDataLayer({ ecommerce: null }) // Clear the previous ecommerce object.

    // Não é necessário colocar esse map para dentro do "items" porque apenas um item entra por vez no carrinho
    roomRates.map((roomRate) => {
      pushToDataLayer({
        event: 'notFoundInRefreshCart',
        location: hotelResult?.hotel?.cityName, // '{{NAME-CITY}}',
        // clientName: null, // '{{CLIENT-NAME}}',
        // clientProject: null, // '{{CLIENT-PROJECT}}',
        ecommerce: {
          currency: roomRate?.priceComposition?.productPrice?.currency, //'{{CURRENCY}}',
          value: roomRate?.priceComposition?.productPrice?.value, // (PRICE-ROOM) * (QUANTITY): NO TAXES
          items: [
            {
              item_id: roomRate?.roomType?.id, // {{ ROOM-ID }}
              item_name: roomRate?.roomType?.name, // {{ ROOM-NAME }}
              item_variant: roomRate?.ratePlan?.name, // {{ ROOM-RATE }}
              item_brand: hotel?.name, // {{ HOTEL-NAME }}

              item_list_id: hotel?.chainId, // {{CHAIN-ID}}
              item_list_name: hotel?.chainName, // {{CHAIN-NAME}}
              check_in: roomRate?.time?.startDate, // {{ CHECK-IN }}
              check_out: roomRate?.time?.endDate, // {{ CHECK-OUT }}

              price: meanBy(roomRate?.priceComposition?.nights, (night) => night.value), // DAILY MAIN PRICE
              discount: roomRate?.priceComposition?.discounts?.value, // {{ DISCOUNT-VALUE}}
              coupon: roomRate?.promoCode, // "{{COUPON-CODE}}",
              quantity: roomRate?.priceComposition?.nights?.length, // NUMBER OF NIGHTS BOOKED

              item_category: 'ROOM', // ROOM OR EXTRA
              item_category2: hotel?.award, // {{N-STAR_HOTEL}}
              item_category3: '', // {{HOTEL-CATEGORY}}

              affiliation: getAffiliation(hotelResult?.hotel?.id) ?? storefront?.name,
              currency: roomRate?.priceComposition?.total?.currency,
            },
          ],
        },
        firstName: buyer?.firstName, // '{{FIRST-NAME}}',
        lastName: buyer?.lastName, // '{{LAST-NAME}}',
        userEmail: buyer?.email, // '{{USER-EMAIL}}',
      })

      facebookTrack('notFoundInRefreshCart', {
        // content_ids,
        // content_name: 'view_item',
        content_type: 'notFoundInRefreshCart',
        contents: {
          currency: roomRate?.priceComposition?.total.currency,
          value: roomRate?.priceComposition?.total.value,
          items: [buildItem(hotelResult, roomRate, 'Hotel Details', undefined)],
        },
        currency: roomRate?.priceComposition?.total.currency,
        value: roomRate?.priceComposition?.total.value,
      })
    })
  }
)

export const trackEventBeginCheckout = catchAnyErrorFunction(
  (shoppingCart: ShoppingCartState, buyer: PersonalData['buyer'] | User, storefront: Storefront): void => {
    // Event 2 - Checkout - Page Load
    // Call this function when user begins checkout

    if (!shoppingCart || !shoppingCart.total || !(shoppingCart.items?.length > 0)) return
    const currency = shoppingCart.total.currency
    const value = shoppingCart.total.value
    const numberRooms = shoppingCart?.items?.reduce((acc, item) => acc + item.quantity ?? 1, 0)
    const chainId = shoppingCart?.items[0]?.hotelResult?.hotel?.chainId
    const hotelId = shoppingCart?.items[0]?.hotelResult?._id

    pushToDataLayer({ ecommerce: null }) // Clear the previous ecommerce object.

    pushToDataLayer({
      event: 'ecommerceCheckout',
      eventDetails: {
        category: 'Ecommerce',
        action: 'Checkout',
        label: 'Passo 1 - Dados Pessoais',
      },
      versionBE: datalayerVersion, // '{{VERSION-BE}}',
      chainName: shoppingCart?.items[0]?.hotelResult?.hotel?.chainName, // '{{CHAIN-NAME}}'FROM THE FIRST CART ITEM
      chainId: clearChainIdToDataLayer(chainId), // '{{CHAIN-ID}}' FROM THE FIRST CART ITEM
      hotelName: shoppingCart?.items[0]?.hotelResult?.hotel?.name, // '{{HOTEL-NAME}}' FROM THE FIRST CART ITEM
      hotelId: clearHotelIdToDataLayer(hotelId), // '{{HOTEL-ID}}' FROM THE FIRST CART ITEM
      checkIn: clearDateToDataLayer(shoppingCart?.items[0]?.startDate),
      checkOut: clearDateToDataLayer(shoppingCart?.items[0]?.endDate),
      coupon: shoppingCart?.items[0]?.promoCode, // '{{COUPON-CODE}}' FROM THE FIRST CART ITEM
      hotelCategoryName: null, // será retornado do hotelDetails '{{HOTEL-CATEGORY-NAME}}',
      hotelCategoryId: null, // será retornado do hotelDetails '{{HOTEL-CATEGORY-ID}}',
      ratingStars: shoppingCart?.items[0]?.hotelResult?.hotel?.award, // '{{NUMBER-STAR}}' FROM THE FIRST CART ITEM
      numberRooms, // NUMBER OF AVAILABLE ROOMS FROM THE FIRST CART ITEM
      numberAdults: sumBy(shoppingCart?.items, (cartItem) => cartItem.occupancy?.adults), // '{{NUMBER-OF-ADULTS}}' TOTAL
      numberChildren: sumBy(shoppingCart?.items, (cartItem) => cartItem.occupancy?.children), // '{{NUMBER-OF-CHILDREN}}' TOTAL
      ...buildBuyerData(buyer, shoppingCart?.personalData?.buyer),
      isLoggedIn: null, // '{{USER_IS_LOGGED}}'
      lang: getLocale(storefront?.i18n_defaultLocale),
      isRemark: null,
      // //TODO
      // loyalty: {
      //   loyalty: '{{LOYALTY}}',
      //   loyalty_category: '{{LOYALTY_CATEGORY}}',
      //   loyalty_level: '{{LOYALTY_LEVEL}}',
      //   loyalty_name: '{{LOYALTY_DESCRIPTION}}',
      //   loyalty_discount_value: '{{VALUE}}',
      //   loyalt_type: '{{LOYALTY_TYPE}}',
      // },
      ecommerce: {
        currency: currency,
        value: value,
        items: shoppingCart.items
          ?.filter((r) => r.type == 'hotel')
          ?.map((cartItem, i) => ({
            affiliation: getAffiliation(cartItem.hotelResult?.hotel?.id) ?? storefront?.name,
            discount: cartItem.roomRates[0]?.priceComposition?.discounts?.value, //{{ DISCOUNT-VALUE}}
            item_brand: cartItem.hotelResult?.hotel?.name, // '{{HOTEL-NAME}}',
            item_category: 'Accommodation', // Accommodation OR ROOM OR EXTRA,
            item_id: cartItem.roomRates[0]?.roomType?.id, // '{{ROOM-ID}}',
            item_name: cartItem.roomRates[0]?.roomType?.name, // '{{ROOM-NAME}}',
            item_variant: cartItem.roomRates[0]?.ratePlan?.name, // {{RATE-ROOM}}
            coupon: cartItem.promoCode, // '{{COUPON-CODE}}',
            price: meanBy(cartItem.roomRates[0]?.priceComposition?.nights, (night) => night.value), // {{PRICE-ROOM}}, DAILY MEAN PRICE
            quantity: cartItem.roomRates[0]?.priceComposition?.nights?.length, //NUMBER OF NIGHTS BOOKED

            // NOT IN DOCUMENT OMNIBEES
            index: i, // sent by niara
            currency: cartItem.roomRates[0]?.priceComposition?.discounts?.currency, // sent by niara / motor omnibees
            item_list_id: clearChainIdToDataLayer(cartItem?.hotelResult?.hotel?.chainId), // sent by niara {{CHAIN-ID}}
            item_list_name: cartItem?.hotelResult?.hotel?.chainName, // sent by niara {{CHAIN-NAME}}
            check_in: clearDateToDataLayer(cartItem?.startDate), // sent by niara {{CHECK-IN}}
            check_out: clearDateToDataLayer(cartItem?.endDate), // sent by niara {{CHECK-OUT}}
            item_category2: cartItem.hotelResult?.hotel?.award, // sent by niara {{N-STAR_HOTEL}}
            item_category3: cartItem.type, // sent by niara {{HOTEL-CATEGORY}}
            location: cartItem.hotelResult?.hotel?.cityName, // sent by niara '{{NAME-CITY}}'
          })),

        // OLD SUPPORT
        currencyCode: currency,
        checkout: {
          actionField: {
            step: 1,
          },
          products: shoppingCart.items
            ?.filter((r) => r.type == 'hotel')
            ?.map((cartItem) => ({
              name: cartItem.roomRates[0]?.roomType?.name, // '{{PRODUCT-NAME}}',
              id: cartItem.roomRates[0]?.roomType?.id, // '{{PRODUCT-SKU}}',
              price: meanBy(cartItem.roomRates[0]?.priceComposition?.nights, (night) => night.value), // '{{PRODUCT-PRICE}}',
              brand: cartItem.hotelResult?.hotel?.chainName, // '{{BRAND}}',
              category: 'Accommodation', // '{{CATEGORY}}',
              quantity: cartItem.roomRates[0]?.priceComposition?.nights?.length, //'{{PRODUCT-QUANTITY}}',
              dimension4: cartItem.roomRates[0]?.ratePlan?.name, //'{{RATE-NAME}}',
              dimension5:
                cartItem.roomRates[0]?.occupancy?.adults ?? 1 + cartItem.roomRates[0]?.occupancy?.children ?? 0, // '{{NUMBER-OF-GUESTS}}',
            })),
          //NOT IN DOC OMNIBEES
          guestData: {
            email: buyer?.email ?? shoppingCart?.personalData?.buyer?.email,
            name: [
              buyer?.firstName ?? shoppingCart?.personalData?.buyer?.firstName,
              buyer?.lastName ?? shoppingCart?.personalData?.buyer?.lastName,
            ]
              .filter(Boolean)
              .join(' '),
            phone: buyer?.phone ?? shoppingCart?.personalData?.buyer?.phone,
          }, // send by niara / motor omnibees
        },
      },

      //NOT IN DOC OMNIBEES
      clientName: storefront?.clientName, // send by niara '{{CLIENT-NAME}}'
      clientProject: storefront?.landingPageId, // send by niara '{{CLIENT-PROJECT}}',
      location: shoppingCart?.items[0]?.hotelResult?.hotel?.cityName, // send by niara '{{NAME-CITY}}' FROM THE FIRST CART ITEM,
      niaraChainId: chainId, // send by niara
      niaraHotelId: hotelId, // send by niara
      check_in: clearDateToDataLayer(shoppingCart?.items[0]?.startDate), //  send by niara '{{CHECK-IN}}' FROM THE FIRST CART ITEM
      check_out: clearDateToDataLayer(shoppingCart?.items[0]?.endDate), //  send by niara '{{CHECK-OUT}}' FROM THE FIRST CART ITEM
      daysAdvance: diffInDays(shoppingCart?.items[0]?.startDate, new Date()), // send by niara {{DAYS-IN-ADVANCE}}
    })

    facebookTrack('InitiateCheckout', {
      // content_category,
      // num_items,
      // content_ids,
      // content_name: 'view_item',
      contents: {
        currency: currency,
        value: value,
        // coupon: 'SUMMER_FUN', // TODO!
        items: shoppingCart.items
          .map((item) => {
            if (item.hotelResult && item.roomRates?.[0])
              return buildItem(item.hotelResult, item.roomRates[0], undefined, undefined)
          })
          .filter(Boolean),
      },
      currency: currency,
      value: value,
    })
  }
)

export const trackEventAddPaymentInfo = catchAnyErrorFunction(
  (
    cartItems: CartItem[],
    clientName: string,
    landingPageId: string,
    storefront: Storefront,
    buyer?: PersonalData['buyer'] | User
  ): void => {
    // Event 3 - Checkout - Payment
    // Call this function when user adds payment info

    if (!cartItems || !(cartItems?.length > 0)) return
    const numberRooms = cartItems?.reduce((acc, item) => acc + item.quantity ?? 1, 0)

    pushToDataLayer({ ecommerce: null }) // Clear the previous ecommerce object.

    pushToDataLayer({
      event: 'ecommerceCheckout',
      versionBE: datalayerVersion, // '{{VERSION-BE}}',
      eventDetails: {
        category: 'Ecommerce',
        action: 'Checkout',
        label: 'Passo 2 - Pagamento',
      },
      hotelName: cartItems[0]?.hotelDetails?.name, // '{{HOTEL-NAME}}' FROM THE FIRST CART ITEM
      hotelId: clearHotelIdToDataLayer(cartItems[0]?.hotelId), // '{{HOTEL-ID}}' FROM THE FIRST CART ITEM
      chainName: cartItems[0]?.hotelDetails?.chainName, // '{{CHAIN-NAME}}'FROM THE FIRST CART ITEM
      chainId: clearChainIdToDataLayer(cartItems[0]?.hotelDetails?.chainId), // '{{CHAIN-ID}}' FROM THE FIRST CART ITEM
      hotelCategoryName: null, // será retornado do hotelDetails '{{HOTEL-CATEGORY-NAME}}',
      hotelCategoryId: null, // será retornado do hotelDetails '{{HOTEL-CATEGORY-ID}}',
      ratingStars: cartItems?.[0]?.hotelDetails?.rating, // '{{NUMBER-STAR}}' FROM THE FIRST CART ITEM
      numberRooms, // NUMBER OF AVAILABLE ROOMS FROM THE FIRST CART ITEM
      checkIn: clearDateToDataLayer(cartItems[0]?.startDate), // '{{CHECK-IN}}' FROM THE FIRST CART ITEM
      checkOut: clearDateToDataLayer(cartItems[0]?.endDate), // '{{CHECK-OUT}}' FROM THE FIRST CART ITEM
      numberAdults: sumBy(cartItems, (cartItem) => cartItem.occupancy?.adults), // '{{NUMBER-OF-ADULTS}}' TOTAL
      numberChildren: sumBy(cartItems, (cartItem) => cartItem.occupancy?.children), // '{{NUMBER-OF-CHILDREN}}' TOTAL
      lang: getLocale(storefront?.i18n_defaultLocale),
      coupon: '', // '{{COUPON-CODE}}' FROM THE FIRST CART ITEM
      isRemark: null, // '{{ISREMARK}}',
      isLoggedIn: null, // '{{USER_IS_LOGGED}}',
      ...buildBuyerData(buyer),
      ecommerce: {
        currency: cartItems[0]?.priceComposition?.total?.currency,
        value: sumBy(cartItems, (cartItem) => cartItem.priceComposition?.total?.value ?? 0),
        payment_type: '',
        items: cartItems?.map((cartItem, i) => ({
          affiliation: getAffiliation(cartItem?.hotelDetails?.hotelId) ?? storefront?.name,
          discount: cartItem.priceComposition?.discounts?.value, //{{ DISCOUNT-VALUE}}
          item_brand: cartItem.hotelDetails?.name, // '{{HOTEL-NAME}}',
          item_category: 'Accommodation', // Accommodation OR ROOM OR EXTRA
          item_id: cartItem.roomRate?.roomType?.id, // '{{ROOM-ID}}',
          item_name: cartItem.roomRate?.roomType?.name, // '{{ROOM-NAME}}',
          item_variant: cartItem.roomRate?.ratePlan?.name, // {{RATE-ROOM}}
          coupon: '', // '{{COUPON-CODE}}',
          price: meanBy(cartItem.priceComposition?.nights, (night) => night.value), // {{PRICE-ROOM}}, DAILY MEAN PRICE
          quantity: cartItem.priceComposition?.nights?.length, //NUMBER OF NIGHTS BOOKED

          //NOT IN DOC OMNIBEES
          index: i, // sent by niara
          currency: cartItem.priceComposition?.discounts?.currency, // sent by niara
          item_list_id: clearChainIdToDataLayer(cartItem?.hotelDetails?.chainId), // sent by niara {{CHAIN-ID}}
          niaraChainId: cartItem?.hotelDetails?.chainId, // sent by niara
          item_list_name: cartItem?.hotelDetails?.chainName, // sent by niara {{CHAIN-NAME}}
          check_in: clearDateToDataLayer(cartItem?.startDate), // sent by niara {{CHECK-IN}}
          check_out: clearDateToDataLayer(cartItem?.endDate), // sent by niara {{CHECK-OUT}}
          checkIn: clearDateToDataLayer(cartItem?.startDate), // sent by niara {{CHECK-IN}}
          checkOut: clearDateToDataLayer(cartItem?.endDate), // sent by niara {{CHECK-OUT}}
          item_category2: cartItem.hotelDetails?.rating, // sent by niara {{N-STAR_HOTEL}}
          item_category3: '', // sent by niara {{HOTEL-CATEGORY}}
          location: cartItem.hotelDetails?.address?.cityName, // sent by niara '{{NAME-CITY}}'
        })),
        //OLD SUPPORT
        currencyCode: cartItems[0]?.priceComposition?.total?.currency, //'{{CURRENCY-CODE}}',
        checkout: {
          actionField: {
            step: 2,
          },
          products: cartItems?.map((cartItem) => ({
            name: cartItem.roomRate?.roomType?.name, // '{{PRODUCT-NAME}}',
            id: cartItem.roomRate?.roomType?.id, // '{{PRODUCT-SKU}}',
            price: meanBy(cartItem.roomRate?.priceComposition?.nights, (night) => night.value), // '{{PRODUCT-PRICE}}',
            brand: cartItem.hotelDetails?.chainName, // '{{BRAND}}',
            category: 'Accommodation', // '{{CATEGORY}}',
            quantity: cartItem.priceComposition?.nights?.length, //'{{PRODUCT-QUANTITY}}',
            dimension4: cartItem.roomRate?.ratePlan?.name, //'{{RATE-NAME}}',
            dimension5: cartItem.roomRate?.occupancy?.adults ?? 1 + cartItem.roomRate?.occupancy?.children ?? 0, // '{{NUMBER-OF-GUESTS}}',
          })),
          //NOT IN DOC OMNIBEES
          guestData: {
            email: buyer?.email,
            name: [buyer?.firstName, buyer?.lastName].filter(Boolean).join(' '),
            phone: buyer?.phone,
          }, // send by niara / motor omnibees
        },
      },

      //NOT IN DOC OMNIBEES
      clientName, // sent by niara '{{CLIENT-NAME}}'
      clientProject: landingPageId, // sent by niara '{{CLIENT-PROJECT}}',
      location: cartItems[0]?.hotelDetails?.address?.cityName, // sent by niara '{{NAME-CITY}}' FROM THE FIRST CART ITEM,
      niaraChainId: clearChainIdToDataLayer(cartItems[0]?.hotelDetails?.chainId), // sent by niara
      niaraHotelId: clearHotelIdToDataLayer(cartItems[0]?.hotelId),
      check_in: clearDateToDataLayer(cartItems[0]?.startDate), // sent by niara '{{CHECK-IN}}' FROM THE FIRST CART ITEM
      check_out: clearDateToDataLayer(cartItems[0]?.endDate), // sent by niara '{{CHECK-OUT}}' FROM THE FIRST CART ITEM
      daysAdvance: diffInDays(cartItems[0]?.startDate, new Date()), // sent by niara {{DAYS-IN-ADVANCE}}
    })

    facebookTrack('AddPaymentInfo', {
      // content_category,
      // content_ids,
      contents: {
        currency: cartItems[0]?.priceComposition?.total?.currency,
        value: cartItems.reduce((sum, cartItem) => cartItem.priceComposition?.total?.value, 0),
        payment_type: '',
        // coupon: 'SUMMER_FUN', // TODO!
        items: cartItems
          .map((item, i) => {
            if (item.hotelDetails && item.roomRate)
              return {
                item_name: item.hotelDetails?.name,
                item_id: item.hotelId,
                price: item.roomRate ? item.roomRate?.priceComposition?.productPrice?.value : undefined,
                currency: item.roomRate ? item.roomRate?.priceComposition?.productPrice?.currency : undefined,
                item_category: 'Hotel',
                item_variant: item.roomRate ? item.roomRate?.time?.startDate : undefined,
                item_list_name: undefined,
                index: i,
                quantity: 1,
              }
          })
          .filter(Boolean),
      },
      currency: cartItems[0]?.priceComposition?.total?.currency,
      value: cartItems.reduce((sum, cartItem) => cartItem.priceComposition?.total?.value, 0),
    })
  }
)

type PaymentSubmitDataV2 = Parameters<PaymentFormV2Props['onSubmit']>[0]

export const trackEventPurchaseClick = catchAnyErrorFunction(
  (
    paymentData: PaymentSubmitDataV2,
    shoppingCart: ShoppingCartState,
    storefront: Storefront,
    buyer: PersonalData['buyer'] | User
  ): void => {
    // Event 4 - Checkout - Click to Confirm
    // Call this function when user clica em comprar

    pushToDataLayer({
      event: 'ecommerceCheckout',
      versionBE: datalayerVersion, // '{{VERSION-BE}}',
      eventDetails: {
        category: 'Ecommerce',
        action: 'Checkout',
        label: 'Passo 3 - Clique - Confirmar Reserva',
      },
      hotelName: shoppingCart?.items[0]?.hotelResult?.hotel?.name, // '{{HOTEL-NAME}}' FROM THE FIRST CART ITEM
      hotelId: clearHotelIdToDataLayer(shoppingCart?.items[0]?.hotelResult?._id), // '{{HOTEL-ID}}' FROM THE FIRST CART ITEM
      chainId: clearChainIdToDataLayer(shoppingCart?.items[0]?.hotelResult?.hotel?.chainId), // '{{CHAIN-ID}}' FROM THE FIRST CART ITEM
      chainName: shoppingCart?.items[0]?.hotelResult?.hotel?.chainName, // '{{CHAIN-NAME}}'FROM THE FIRST CART ITEM
      hotelCategoryName: null, // será retornado do hotelDetails '{{HOTEL-CATEGORY-NAME}}',
      hotelCategoryId: null, // será retornado do hotelDetails '{{HOTEL-CATEGORY-ID}}',
      ratingStars: shoppingCart?.items?.[0]?.hotelResult?.hotel?.award, // '{{NUMBER-STAR}}' FROM THE FIRST CART ITEM
      numberRooms: sumBy(shoppingCart?.items, (cartItem) => cartItem?.quantity ?? 1), // NUMBER OF AVAILABLE ROOMS FROM THE FIRST CART ITEM
      checkIn: clearDateToDataLayer(shoppingCart?.items[0]?.startDate), // '{{CHECK-IN}}' FROM THE FIRST CART ITEM
      checkOut: clearDateToDataLayer(shoppingCart?.items[0]?.endDate), // '{{CHECK-OUT}}' FROM THE FIRST CART ITEM
      numberAdults: sumBy(shoppingCart?.items, (cartItem) => cartItem.occupancy?.adults), // '{{NUMBER-OF-ADULTS}}' TOTAL
      numberChildren: sumBy(shoppingCart?.items, (cartItem) => cartItem.occupancy?.children), // '{{NUMBER-OF-CHILDREN}}' TOTAL
      lang: getLocale(storefront?.i18n_defaultLocale),
      coupon: shoppingCart?.items[0]?.promoCode, // '{{COUPON-CODE}}' FROM THE FIRST CART ITEM
      isRemark: null, // '{{ISREMARK}}',
      isLoggedIn: null, // '{{USER_IS_LOGGED}}',
      ...buildBuyerData(buyer, shoppingCart?.personalData?.buyer),
      ecommerce: {
        payment_type:
          'payments' in paymentData
            ? paymentData?.payments?.[0]?.paymentOption?.alias
            : paymentData?.paymentOption?.alias, // '{{PAYMENT-METHOD}}',
        currency: shoppingCart.total.currency, // CURRENCY
        value: shoppingCart?.total?.value, // '{{TRANSACTION-TOTAL}}',
        coupon: shoppingCart?.items[0]?.promoCode, //  '{{PROMO-CODE}}',
        items: shoppingCart.items?.map((cartItem, i) => ({
          affiliation: getAffiliation(cartItem?.hotelResult?.hotel?.id) ?? storefront?.name,
          discount: cartItem.roomRates[0]?.priceComposition?.discounts?.value, //{{ DISCOUNT-VALUE}}
          item_brand: cartItem.hotelResult?.hotel?.name, // '{{HOTEL-NAME}}',
          item_category: 'Accommodation', // Accommodation OR ROOM OR EXTRA
          item_id: cartItem.roomRates[0]?.roomType?.id, // '{{ROOM-ID}}',
          item_name: cartItem.roomRates[0]?.roomType?.name, // '{{ROOM-NAME}}',
          item_variant: cartItem.roomRates[0]?.ratePlan?.name, // {{RATE-ROOM}}
          coupon: cartItem.promoCode, // '{{COUPON-CODE}}',
          price: meanBy(cartItem.roomRates[0]?.priceComposition?.nights, (night) => night.value), // {{PRICE-ROOM}}, DAILY MEAN PRICE
          quantity: cartItem.roomRates[0]?.priceComposition?.nights?.length, //NUMBER OF NIGHTS BOOKED

          //NOT IN DOC OMNIBEES
          index: i, // sent by niara
          currency: cartItem.roomRates[0]?.priceComposition?.discounts?.currency, // sent by niara
          item_list_id: clearChainIdToDataLayer(cartItem?.hotelResult?.hotel?.chainId), // sent by niara {{CHAIN-ID}}
          niaraChainId: cartItem?.hotelResult?.hotel?.chainId, // sent by niara {{CHAIN-ID}}
          item_list_name: cartItem?.hotelResult?.hotel?.chainName, // sent by niara {{CHAIN-NAME}}
          check_in: clearDateToDataLayer(cartItem?.startDate), // sent by niara {{CHECK-IN}}
          check_out: clearDateToDataLayer(cartItem?.endDate), // sent by niara {{CHECK-OUT}}
          checkIn: clearDateToDataLayer(cartItem?.startDate), // sent by niara {{CHECK-IN}}
          checkOut: clearDateToDataLayer(cartItem?.endDate), // sent by niara {{CHECK-OUT}}
          item_category2: cartItem.hotelResult?.hotel?.award, // sent by niara {{N-STAR_HOTEL}}
          item_category3: cartItem.type, // {{HOTEL-CATEGORY}}
          location: cartItem.hotelResult?.hotel?.cityName, // '{{NAME-CITY}}'
        })),
        //OLD SUPPORT
        currencyCode: shoppingCart.total.currency, // '{{CURRENCY-CODE}}',
        checkout: {
          actionField: {
            step: 3,
            option:
              'payments' in paymentData
                ? paymentData?.payments?.[0]?.paymentOption?.alias
                : paymentData?.paymentOption?.alias, //'{{PAYMENT-METHOD}}',
          },
          products: shoppingCart.items?.map((cartItem) => ({
            // 'products' é a lista de acomodações e extras adicionados nas páginas anteriores
            name: cartItem.roomRates[0]?.roomType?.name, // '{{PRODUCT-NAME}}',
            id: cartItem.roomRates[0]?.roomType?.id, // '{{PRODUCT-SKU}}',
            price: meanBy(cartItem.roomRates[0]?.priceComposition?.nights, (night) => night.value), // '{{PRODUCT-PRICE}}',
            brand: cartItem.hotelResult?.hotel?.chainName, // '{{BRAND}}',
            category: 'Accommodation', // '{{CATEGORY}}' Accommodation OR EXTRA OR ROOM,
            quantity: cartItem.roomRates[0]?.priceComposition?.nights?.length, //'{{PRODUCT-QUANTITY}}',
            dimension4: cartItem.roomRates[0]?.ratePlan?.name, //'{{RATE-NAME}}',
            dimension5: cartItem.roomRates[0]?.occupancy?.adults ?? 1 + cartItem.roomRates[0]?.occupancy?.children ?? 0, // '{{NUMBER-OF-GUESTS}}',
          })),
          //NOT IN DOC OMNIBEES
          guestData: {
            email: shoppingCart?.personalData?.buyer?.email,
            name: [shoppingCart?.personalData?.buyer?.firstName, shoppingCart?.personalData?.buyer?.lastName]
              .filter(Boolean)
              .join(' '),
            phone: shoppingCart?.personalData?.buyer?.phone,
          }, // send by niara / motor omnibees
        },
      },

      //NOT IN DOC OMNIBEES
      clientName: storefront?.clientName, // sent by niara '{{CLIENT-NAME}}'
      clientProject: storefront?.landingPageId, // sent by niara '{{CLIENT-PROJECT}}',
      location: shoppingCart?.items[0]?.hotelResult?.hotel?.cityName, // sent by niara '{{NAME-CITY}}' FROM THE FIRST CART ITEM,
      niaraHotelId: shoppingCart?.items[0]?.hotelResult?._id, // sent by niara '{{HOTEL-ID}}' FROM THE FIRST CART ITEM
      niaraChainId: shoppingCart?.items[0]?.hotelResult?.hotel?.chainId, // sent by niara '{{CHAIN-ID}}' FROM THE FIRST CART ITEM
      check_in: clearDateToDataLayer(shoppingCart?.items[0]?.startDate), // sent by niara '{{CHECK-IN}}' FROM THE FIRST CART ITEM
      check_out: clearDateToDataLayer(shoppingCart?.items[0]?.endDate), // sent by niara '{{CHECK-OUT}}' FROM THE FIRST CART ITEM
      daysAdvance: diffInDays(shoppingCart?.items[0]?.startDate, new Date()), // sent by niara {{DAYS-IN-ADVANCE}}
    })
  }
)

export const trackEventPurchaseSuccess = catchAnyErrorFunction(
  (
    order: Order | SelfOrder,
    hotelReservations: SelfHotelReservation[],
    hotelDetails: HotelDetails | SelfOrder['hotelReservations'][number]['hotelDetails'] | null,
    storefront: Storefront
  ): void => {
    // Event 5 - Success Booking
    // Call this function when purchase is succeded

    const currency = order?.hotelReservations?.[0]?.currencyCode
    const value = order?.hotelReservations?.reduce((acc, item) => acc + (item.totalPrice || 0), 0)

    pushToDataLayer({ ecommerce: null }) // Clear the previous ecommerce object.

    pushToDataLayer({
      event: 'ecommercePurchase',
      versionBE: datalayerVersion, // '{{VERSION-BE}}',
      eventDetails: {
        category: 'Ecommerce',
        action: 'Purchase',
      },
      chainName: hotelDetails?.chainName, // '{{CHAIN-NAME}}',
      chainId: clearChainIdToDataLayer(hotelDetails?.chainId), // '{{CHAIN-ID}}',
      checkIn: clearDateToDataLayer(hotelReservations[0]?.startDate), // '{{CHECK-IN}}', FROM FIRST RESERVATION
      checkOut: clearDateToDataLayer(hotelReservations[0]?.endDate), // '{{CHECK-OUT}}', FROM FIRST RESERVATION
      coupon: hotelReservations[0]?.promoCode,
      firstName: order?.buyer_name.split(' ')[0], // '{{FIRST-NAME}}',
      hotelCategoryId: null, //TODO pegar do hotelDetails, quando disponível.
      hotelCategoryName: null, //TODO pegar do hotelDetails, quando disponível.
      hotelId: clearHotelIdToDataLayer(hotelReservations[0]?.hotelId), // '{{HOTEL-ID}}', FROM FIRST RESERVATION
      hotelName: hotelReservations[0]?.hotelName, // '{{HOTEL-NAME}}', FROM FIRST RESERVATION
      isRemark: null,
      isLoggedIn: null, //'{{USER_IS_LOGGED}}'
      lang: getLocale(storefront?.i18n_defaultLocale),
      lastName: order?.buyer_name.split(' ').slice(1).join(' '), // '{{LAST-NAME}}',
      //TODO ver como pegar informações do loyalty
      // loyalty: {
      //   loyalty: null, // {{LOYALTY}} enviado pelo motor omnibees
      //   loyalty_discount_value: null, // {{VALUE}} enviado pelo motor omnibees
      //   loyalty_level: null, // {{LOYALTY_LEVEL}} enviado pelo motor omnibees
      //   loyalty_name: null, // {{LOYALTY_DESCRIPTION}} enviado pelo motor omnibees
      //   loyalty_category: null, // {{LOYALTY_CATEGORY}} não enviado pelo motor omnibees, mas existe na documentação omnibees
      //   loyalt_type: null, // {{LOYALTY_TYPE}} não enviado pelo motor omnibees, mas existe na documentação omnibees
      //   //NOT IN DOC OMNIBEES
      //   first_time_loyalty: null, // '1' enviado pelo motor omnibees
      //   loyalty_discount_type: null, // 'percente' enviado pelo motor omnibees
      //   loyalty_level_id: null, // '166' enviado pelo motor omnibees
      // },
      numberAdults: sumBy(
        hotelReservations?.map((r) => r.roomStays).flat(1),
        (roomStay) => roomStay.occupancy?.adults ?? 0
      ), // '{{NUMBER-OF-ADULTS}}', TOTAL
      numberChildren: sumBy(
        hotelReservations?.map((r) => r.roomStays).flat(1),
        (roomStay) => roomStay?.occupancy?.children ?? 0
      ), // '{{NUMBER-OF-CHILDREN}}', TOTAL
      numberRooms: sumBy(
        hotelReservations?.map((r) => r?.roomStays?.length ?? 0).flat(1),
        (roomStaysLength) => roomStaysLength ?? 0
      ), //'{{TOTAL-ROOMS-OF-HOTEL}}'
      ratingStars: hotelReservations[0]?.hotelDetails?.rating, // '{{NUMBER-STAR}}'
      userCountry: order?.buyer_country, // '{{USER-COUNTRY}}',
      userDoc: order?.buyer_cpf, // '{{USER-DOC}}',
      userEmail: order?.buyer_email, // '{{USER-EMAIL}}',
      userId: null, // '{{USER-ID}}'
      dimension1: null, // '{{USER-ID}}'
      userTel: order?.buyer_phone, //  '{{USER-TEL}}',
      userNationality: order?.buyer_nationality,
      userAddressCountryCode: null, // TODO como pegar essa informação? #232666
      userAddressCity: order?.buyer_addressCity,
      userAddressComplement: order?.buyer_addressComplement,
      userAddressDistrict: order?.buyer_addressDistrict,
      userAddressNumber: order?.buyer_addressNumber,
      userAddressState: order?.buyer_addressState,
      userAddressStreet: order?.buyer_addressStreet,
      userAddressZipCode: order?.buyer_addressZipCode,
      userDob: order?.buyer_dob,
      userPassport: order?.buyer_passport,
      userPersonId: order?.buyer_personId,
      userRg: null, // TODO como pegar essa informação? #232666
      userRgOrgaoEmissor: null, // TODO como pegar essa informação? #232666
      ecommerce: {
        value: hotelReservations?.reduce((sum, reservation) => sum + reservation?.totalPrice, 0), // {{TOTAL}},
        payment_type: (order as SelfOrder)?.payments?.[0]?.type, // '{{PAYMENT-METHOD}}',
        transaction_id:
          hotelReservations?.length > 0 ? hotelReservations.map((hr) => hr.locator ?? hr.id).join(',') : order?.id, // '{{RESERVATION-ID}}',
        tax: hotelReservations?.reduce((sum, reservation) => sum + reservation?.totalTaxesPrice, 0), // {{TAX}},
        currency: hotelReservations[0]?.currencyCode, // '{{CURRENCY-CODE}}',
        currencyCode: hotelReservations[0]?.currencyCode, // '{{CURRENCY-CODE}}',

        items: hotelReservations?.map((reservation, i) => ({
          affiliation: getAffiliation(reservation?.hotelId) ?? storefront?.name,
          coupon: reservation.promoCode,
          currency: reservation.roomStays[0]?.price?.total?.currency,
          discount: sumBy(
            hotelReservations?.map((r) => r?.roomStays?.map((rs) => rs?.price?.discountBreakdown))?.flat?.(2),
            (discountBreakdown) => discountBreakdown?.value ?? 0
          ),
          item_brand: reservation.hotelName, // '{{HOTEL-NAME}}', PODE SER QUE NÃO SEJA DO HOTEL DESSA RESERVA, TERIA QUE TER NO RESERVATION
          item_category: 'Accommodation', // Accommodation OR ROOM OR EXTRA
          item_id: reservation.locator ?? reservation.id, // '{{ROOM-ID}}',
          item_name: reservation.roomStays[0]?.roomDescription, //'{{ROOM-NAME}}',
          item_variant: reservation?.roomStays?.[0]?.ratePlanName, //  '{{RATE-ROOM}}',
          price: meanBy(reservation.roomStays[0]?.price?.nights, (night) => night.value), // {{PRICE-ROOM}}, DAILY MEAN PRICE
          quantity: reservation.roomStays[0]?.price?.nights?.length, // NUMBER OF NIGHTS BOOKED

          //NOT IN DOC OMNIBEES
          index: i, // sent by niara
          location: reservation.hotelCityName ?? hotelDetails?.address?.cityName, // sent by niara '{{NAME-CITY}}'
          item_category2: hotelDetails?.rating, // sent by niara {{N-STAR_HOTEL}}
          item_category3: null, // sent by niara {{HOTEL-CATEGORY}}
          item_list_id: clearChainIdToDataLayer(hotelDetails?.chainId), // sent by niara {{CHAIN-ID}}
          niaraChainId: hotelDetails?.chainId, // sent by niara {{CHAIN-ID}}
          item_list_name: hotelDetails?.chainName, // sent by niara {{CHAIN-NAME}}
          check_in: clearDateToDataLayer(reservation.startDate), // sent by niara
          check_out: clearDateToDataLayer(reservation.endDate), // sent by niara
          checkIn: clearDateToDataLayer(reservation.startDate), // sent by niara
          checkOut: clearDateToDataLayer(reservation.endDate), // sent by niara
        })),
        //OLD SUPPORT
        // Segundo Thallyson, podemos remover o objeto purchase.
        purchase: {
          actionField: {
            id:
              hotelReservations?.length > 0 ? hotelReservations.map((hr) => hr.locator ?? hr.id).join(',') : order?.id, // '{{RESERVATION-ID}}',
            revenue: hotelReservations?.reduce((sum, reservation) => sum + reservation?.totalPrice, 0), // '{{TOTAL-REVENUE}}',
            tax: hotelReservations?.reduce((sum, reservation) => sum + reservation?.totalTaxesPrice, 0), //'{{TAX}}',
            shipping: '0',
            affiliation: '',
            coupon: hotelReservations?.[0].promoCode, //'{{COUPON-CODE}}',
            currency: hotelReservations?.[0]?.currencyCode, // '{{CURRENCY-CODE}}',
          },
          products: hotelReservations?.map((reservation) => ({
            name: reservation.roomStays[0]?.roomDescription, //'{{PRODUCT-NAME}}',
            id: reservation.roomStays[0]?.roomTypeId, // '{{PRODUCT-SKU}}',
            price: meanBy(reservation.roomStays[0]?.price?.nights, (night) => night.value), // '{{PRODUCT-PRICE}}',
            category: 'Accommodation', // '{{CATEGORY}}',
            brand: reservation.hotelName, // '{{BRAND}}',
            quantity: reservation.roomStays[0]?.price?.nights?.length, //  '{{PRODUCT-QUANTITY}}',
            dimension4: reservation?.roomStays?.[0]?.ratePlanName, //'{{RATE-NAME}}',
            dimension5: sumBy(
              reservation?.roomStays,
              (roomStay) => roomStay.occupancy?.adults ?? 0 + roomStay.occupancy?.children ?? 0
            ), //'{{NUMBER-OF-GUESTS}}',
            //NOT IN DOC OMNIBEES
            currency: reservation?.currencyCode, // '{{CURRENCY-CODE}}', // sent by niara / enviado pelo motor omnibees
          })),
        },
      },

      //NOT IN DOC OMNIBEES
      check_in: clearDateToDataLayer(hotelReservations[0]?.startDate), // sent by niara '{{CHECK-IN}}', FROM FIRST RESERVATION
      check_out: clearDateToDataLayer(hotelReservations[0]?.endDate), // sent by niara '{{CHECK-OUT}}', FROM FIRST RESERVATION
      niaraChainId: hotelDetails?.chainId, // sent by niara
      niaraHotelId: hotelReservations[0]?.hotelId, // sent by niara '{{HOTEL-ID}}', FROM FIRST RESERVATION
      clientName: storefront?.clientName, // sent by niara
      clientProject: storefront?.landingPageId, // sent by niara
      location: hotelReservations[0]?.hotelCityName, // sent by niara '{{NAME-CITY}}', FROM FIRST RESERVATION
      daysAdvance: diffInDays(hotelReservations[0]?.startDate, new Date()), // sent by niara {{DAYS-IN-ADVANCE}}
    })

    facebookTrack('Purchase', {
      content_type: 'purchase',
      contents: {
        transaction_id:
          hotelReservations?.length > 0 ? hotelReservations.map((hr) => hr.locator ?? hr.id).join(',') : order?.id, // '{{RESERVATION-ID}}',
        value,
        currency,
        items: order?.hotelReservations?.map((item) => buildItem2(item)).filter(Boolean),
      },
      currency: currency,
      value: value,
    })
  }
)

export const trackEventError = catchAnyErrorFunction(
  (errorId: string, message: string, step: string, storefront: Storefront): void => {
    // Other events - Error Events
    // Call this function when an error occurs on checkout page

    pushToDataLayer({
      event: 'exception',
      clientName: storefront?.clientName, // '{{CLIENT-NAME}}'
      clientProject: storefront?.landingPageId, // '{{CLIENT-PROJECT}}',
      location: '', // '{{NAME-CITY}}',
      versionBE: datalayerVersion, // '{{VERSION-BE}}',
      eventDetails: {
        id: errorId, // '{{ERROR-ID}}',
        category: 'Ecommerce',
        step: step, // '{{ERROR-STEP-PAGE}}',
        description: message, // '{{ERROR-DESCRIPTION}}',
        fatal: false, // {{TRUE/FALSE}}
      },
    })
  }
)
